import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  KeyRound,
  Database,
  Users,
  Server,
  FileCheck2,
  Eye,
  Trash2,
  Mail,
  CreditCard,
  Cloud,
  CircleDot,
} from 'lucide-react';
import { Logo } from '../components/Logo';

// -------------------------------------------------------------
// Trust Center — enterprise transparency page
// Reflects the live ShiftCover365 platform only. Do not list
// providers/practices that aren't actually in use.
// -------------------------------------------------------------

const SUBPROCESSORS = [
  {
    provider: 'Resend',
    icon: Mail,
    purpose: 'Transactional email delivery (account verification, password reset, welcome emails)',
    region: 'United States',
    data: 'Recipient email address, full name, message content',
    reviewed: 'Feb 2026',
  },
  {
    provider: 'Stripe',
    icon: CreditCard,
    purpose: 'Payment processing, saved payment-method tokens, branch billing',
    region: 'United States (UK & EU regional processing)',
    data: 'Stripe customer ID, payment-method ID, billing email',
    reviewed: 'Feb 2026',
  },
  {
    provider: 'Emergent',
    icon: Cloud,
    purpose: 'Application hosting, infrastructure, object storage for profile photos',
    region: 'Global cloud infrastructure',
    data: 'Application traffic, profile photo binaries',
    reviewed: 'Feb 2026',
  },
  {
    provider: 'MongoDB',
    icon: Database,
    purpose: 'Primary database storage for application records',
    region: 'Managed cloud region',
    data: 'User accounts, shifts, ratings, audit logs, accountability records',
    reviewed: 'Feb 2026',
  },
];

const SECURITY_PRACTICES = [
  { icon: Lock, title: 'Password hashing', body: 'All passwords are stored as bcrypt hashes. Plain-text passwords are never logged or persisted.' },
  { icon: KeyRound, title: 'JWT authentication', body: 'Sessions are signed JWTs (HS256, 24-hour expiry). Tokens live in browser local storage and are cleared on sign-out.' },
  { icon: Users, title: 'Role-based access control', body: 'Manager, worker and admin roles are enforced server-side on every protected endpoint.' },
  { icon: FileCheck2, title: 'Operational audit logging', body: 'Account creation, deletion, payment attempts and key shift state changes are recorded in tamper-evident system logs.' },
  { icon: ShieldCheck, title: 'Encrypted payment handling', body: 'Card data is collected and stored exclusively by Stripe. ShiftCover365 only stores reference tokens, never card numbers.' },
];

const TRANSPARENCY_POINTS = [
  { icon: Trash2, title: 'Soft-delete behaviour', body: 'When you delete your account, it is marked as deleted and sign-in is blocked. Operational records are retained where necessary for audit, dispute resolution, fraud prevention and legal obligations.' },
  { icon: Server, title: 'Operational retention', body: 'Shift records, ratings, accountability data, till checks, discrepancy reports and payment history are retained in full. Identifying personal data can be anonymised on a verified erasure request.' },
  { icon: Eye, title: 'Support & admin access', body: 'A small number of authorised ShiftCover365 administrators may view operational data for support, fraud-prevention, dispute-resolution and platform-health purposes. All admin actions are logged.' },
];

const TRUST_OVERVIEW = [
  { icon: ShieldCheck, label: 'Operational accountability' },
  { icon: Lock, label: 'Secure authentication' },
  { icon: FileCheck2, label: 'Audit logging' },
  { icon: Users, label: 'Workforce reliability monitoring' },
  { icon: ShieldCheck, label: 'UK GDPR-aligned operations' },
];

const LEGAL_LINKS = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Cookies', href: '/cookies' },
  { label: 'Help & Legal', href: '/help-legal' },
  { label: 'Trust Center', href: '/trust' },
];

export default function TrustCenterPage() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="trust-center-page">
      {/* Header */}
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="light" linkTo="/" />
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-[12.5px] text-white/70 hover:text-white transition-colors"
            data-testid="trust-back-home"
          >
            Back to home <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0B132B] text-white border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 pt-12 sm:pt-16 pb-14 sm:pb-20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#DA291C] mb-4">
            Trust Center
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight max-w-3xl">
            How ShiftCover365 keeps shift operations secure, accountable and transparent.
          </h1>
          <p className="mt-5 text-[15px] sm:text-base text-slate-300/85 leading-relaxed max-w-2xl">
            A factual overview of how the live platform handles authentication, data, payments and operational records — written for procurement, security and legal reviewers.
          </p>

          {/* Status pill */}
          <Link
            to="/status"
            className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-sm pl-3 pr-5 py-2 transition-colors group"
            data-testid="trust-status-pill"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[12.5px] font-medium text-slate-100">System operational</span>
            <span className="text-[11px] text-slate-400 group-hover:text-slate-300 transition-colors">· View status</span>
          </Link>
        </div>
      </section>

      {/* Body */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-14 sm:py-20 space-y-16 sm:space-y-20">

          {/* 1. Platform Trust Overview */}
          <section data-testid="trust-overview-section">
            <SectionHeader eyebrow="01 — Overview" title="Platform trust overview" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mt-8">
              {TRUST_OVERVIEW.map((p) => {
                const I = p.icon;
                return (
                  <div
                    key={p.label}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-4 flex items-center gap-3 hover:border-slate-300 transition-colors"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B132B]/5 shrink-0">
                      <I className="h-4 w-4 text-[#0B132B]" strokeWidth={1.75} />
                    </span>
                    <span className="text-[13px] font-semibold text-slate-900 leading-snug">{p.label}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 2. Subprocessors */}
          <section data-testid="trust-subprocessors-section">
            <SectionHeader
              eyebrow="02 — Subprocessors"
              title="Named subprocessors currently in use"
              description="These are the third-party services that help us operate ShiftCover365 today. We do not currently use any analytics, advertising or marketing processor."
            />

            {/* Desktop table */}
            <div className="mt-8 rounded-2xl border border-slate-200 overflow-hidden bg-white hidden md:block">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80 border-b border-slate-200">
                  <tr className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                    <th className="px-5 py-3.5">Provider</th>
                    <th className="px-5 py-3.5">Purpose</th>
                    <th className="px-5 py-3.5">Region</th>
                    <th className="px-5 py-3.5">Data processed</th>
                    <th className="px-5 py-3.5 whitespace-nowrap">Last reviewed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {SUBPROCESSORS.map((s) => {
                    const I = s.icon;
                    return (
                      <tr key={s.provider} className="text-[13.5px] text-slate-700 hover:bg-slate-50/60 transition-colors" data-testid={`subprocessor-row-${s.provider.toLowerCase()}`}>
                        <td className="px-5 py-4 align-top">
                          <span className="inline-flex items-center gap-2.5">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0B132B]/5 shrink-0">
                              <I className="h-4 w-4 text-[#0B132B]" strokeWidth={1.75} />
                            </span>
                            <span className="font-semibold text-slate-900">{s.provider}</span>
                          </span>
                        </td>
                        <td className="px-5 py-4 align-top leading-relaxed">{s.purpose}</td>
                        <td className="px-5 py-4 align-top whitespace-nowrap">{s.region}</td>
                        <td className="px-5 py-4 align-top leading-relaxed">{s.data}</td>
                        <td className="px-5 py-4 align-top text-slate-500 whitespace-nowrap">{s.reviewed}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-8 space-y-3 md:hidden">
              {SUBPROCESSORS.map((s) => {
                const I = s.icon;
                return (
                  <div key={s.provider} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B132B]/5 shrink-0">
                        <I className="h-4 w-4 text-[#0B132B]" strokeWidth={1.75} />
                      </span>
                      <h4 className="text-[15px] font-bold text-slate-900">{s.provider}</h4>
                    </div>
                    <dl className="mt-3 grid grid-cols-3 gap-x-3 gap-y-3 text-[13px]">
                      <dt className="col-span-1 text-slate-500 font-medium">Purpose</dt>
                      <dd className="col-span-2 text-slate-800">{s.purpose}</dd>
                      <dt className="col-span-1 text-slate-500 font-medium">Region</dt>
                      <dd className="col-span-2 text-slate-800">{s.region}</dd>
                      <dt className="col-span-1 text-slate-500 font-medium">Data</dt>
                      <dd className="col-span-2 text-slate-800">{s.data}</dd>
                      <dt className="col-span-1 text-slate-500 font-medium">Reviewed</dt>
                      <dd className="col-span-2 text-slate-800">{s.reviewed}</dd>
                    </dl>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 3. Data Security */}
          <section data-testid="trust-security-section">
            <SectionHeader
              eyebrow="03 — Security"
              title="Data security in practice"
              description="ShiftCover365 does not currently hold formal certifications (such as ISO 27001 or SOC 2). The controls below describe what is actually implemented today."
            />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SECURITY_PRACTICES.map((p) => {
                const I = p.icon;
                return (
                  <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition-colors">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B132B] shrink-0">
                      <I className="h-4 w-4 text-white" strokeWidth={1.75} />
                    </span>
                    <h4 className="mt-4 text-[14.5px] font-bold text-slate-900">{p.title}</h4>
                    <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{p.body}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 4. Operational Transparency */}
          <section data-testid="trust-transparency-section">
            <SectionHeader
              eyebrow="04 — Transparency"
              title="Operational transparency"
            />
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {TRANSPARENCY_POINTS.map((p) => {
                const I = p.icon;
                return (
                  <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#DA291C]/8 shrink-0">
                      <I className="h-4 w-4 text-[#DA291C]" strokeWidth={1.75} />
                    </span>
                    <h4 className="mt-4 text-[14.5px] font-bold text-slate-900">{p.title}</h4>
                    <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{p.body}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 5. GDPR & Privacy */}
          <section data-testid="trust-gdpr-section">
            <SectionHeader eyebrow="05 — GDPR & Privacy" title="UK GDPR alignment" />
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 grid sm:grid-cols-2 gap-x-8 gap-y-5">
              <GdprPoint title="UK GDPR & DPA 2018" body="The platform is operated in line with the UK General Data Protection Regulation and the Data Protection Act 2018." />
              <GdprPoint title="Lawful bases" body="Contract necessity, legitimate interests, legal obligation and consent — applied per processing activity." />
              <GdprPoint title="Data subject requests" body="Access, correction, deletion, restriction, objection, portability and consent withdrawal. We respond within one month, in line with UK GDPR." />
              <GdprPoint title="ICO complaint rights" body="You can complain to the UK Information Commissioner's Office at any time. We will cooperate fully with any formal inquiry." />

              <div className="sm:col-span-2 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-3">
                <span className="text-[12.5px] text-slate-500">Related documents:</span>
                <TrustLink href="/privacy">Privacy Policy</TrustLink>
                <TrustLink href="/terms">Terms &amp; Conditions</TrustLink>
                <TrustLink href="/cookies">Cookie Policy</TrustLink>
                <TrustLink href="/help-legal">Help &amp; Legal</TrustLink>
              </div>
            </div>
          </section>

          {/* 6. Status */}
          <section data-testid="trust-status-section">
            <SectionHeader eyebrow="06 — Status" title="System status" />
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 shrink-0">
                  <CircleDot className="h-5 w-5 text-emerald-600" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-[15px] font-bold text-slate-900">All systems operational</p>
                  <p className="text-[13px] text-slate-500 mt-0.5">Live component health and incident history.</p>
                </div>
              </div>
              <Link
                to="/status"
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 px-4 h-10 text-[12.5px] font-semibold transition-colors shrink-0"
                data-testid="trust-status-view-btn"
              >
                View live status
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </section>

          {/* Contact strip */}
          <section data-testid="trust-contact-section">
            <div className="rounded-2xl border border-slate-200 bg-[#0B132B] text-white p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <h3 className="text-lg sm:text-xl font-bold tracking-tight">Security or procurement questions?</h3>
                <p className="text-[13.5px] text-slate-300/85 mt-1.5 max-w-xl leading-relaxed">
                  We respond to vendor reviews, DPIAs and supplier-security questionnaires from prospective branch partners.
                </p>
              </div>
              <a
                href="mailto:support@shiftcover365.co.uk?subject=Trust%20Center%20enquiry"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#0B132B] hover:bg-slate-100 px-5 h-11 text-[13px] font-semibold transition-colors shrink-0"
                data-testid="trust-contact-cta"
              >
                <Mail className="h-4 w-4" />
                Contact security
              </a>
            </div>
          </section>
        </div>
      </main>

      {/* Footer legal nav */}
      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[12.5px]" data-testid="trust-footer-nav">
            {LEGAL_LINKS.map((l, i) => (
              <span key={l.href} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-slate-300">|</span>}
                <Link
                  to={l.href}
                  className="text-slate-500 hover:text-slate-900 transition-colors"
                  data-testid={`trust-footer-link-${l.href.replace('/', '')}`}
                >
                  {l.label}
                </Link>
              </span>
            ))}
          </nav>
          <p className="text-[11.5px] text-slate-400">© {new Date().getFullYear()} ShiftCover365 · All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// --- helpers ---

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#DA291C] mb-3">{eyebrow}</p>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">{title}</h2>
      {description && (
        <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{description}</p>
      )}
    </div>
  );
}

function GdprPoint({ title, body }) {
  return (
    <div>
      <h4 className="text-[14px] font-bold text-slate-900">{title}</h4>
      <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function TrustLink({ href, children }) {
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0B132B] hover:text-[#DA291C] transition-colors"
    >
      {children}
      <ArrowRight className="h-3 w-3" />
    </Link>
  );
}
